import React, { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { useUser } from '../../shared/context/UserContext';
import { apiFetch } from '../../auth/apiFetch';
import './DataOpsScreen.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

/* ═══════════════════════════════════════════════════════════════════════════ */

function EowCard() {
  const { callerEmail } = useUser();
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showSkipped, setShowSkipped] = useState(false);

  async function handleRun() {
    setRunning(true);
    setResult(null);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/calculate_eow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ caller_email: callerEmail }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResult({ ...data, timestamp: new Date().toLocaleString() });
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="do-card">
      <div className="do-card-header">
        <h2 className="do-card-title">EOW Calculation Engine</h2>
        <p className="do-card-sub">
          Recalculates end-of-warranty dates for all Autoliv lifecycle profiles.
        </p>
      </div>

      <button
        className="do-btn-primary"
        onClick={handleRun}
        disabled={running}
      >
        {running ? 'Running...' : 'Run Engine'}
      </button>

      {error && <div className="do-error">{error}</div>}

      {result && (
        <div className="do-result">
          <div className="do-result-row">
            <span className="do-result-label">Calculated</span>
            <span className={`do-result-value ${result.calculated > 0 ? 'green' : 'muted'}`}>
              {result.calculated}
            </span>
          </div>
          <div className="do-result-row">
            <span className="do-result-label">Skipped</span>
            <span className="do-result-value muted">{result.skipped}</span>
          </div>
          <div className="do-result-row">
            <span className="do-result-label">Status</span>
            <span className={`do-badge ${result.status === 'COMPLETE' ? 'do-badge-success' : 'do-badge-danger'}`}>
              {result.status}
            </span>
          </div>
          <div className="do-result-timestamp">Run completed: {result.timestamp}</div>

          {result.skipped_tool_ids && result.skipped_tool_ids.length > 0 && (
            <>
              <button
                className="do-skipped-toggle"
                onClick={() => setShowSkipped(prev => !prev)}
              >
                {showSkipped ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {showSkipped ? 'Hide' : 'Show'} skipped tool IDs ({result.skipped_tool_ids.length})
              </button>
              {showSkipped && (
                <div className="do-skipped-list">
                  {result.skipped_tool_ids.map(id => (
                    <div key={id}>{id}</div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */

function DemandImportCard() {
  return (
    <div className="do-card disabled">
      <div className="do-card-header">
        <h2 className="do-card-title">
          Autoliv Demand Import
          <span className="do-badge do-badge-muted">Coming Soon</span>
        </h2>
        <p className="do-card-sub">Import quarterly demand forecast file.</p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */

export default function DataOpsScreen() {
  return (
    <div className="do-screen">
      <h1 className="do-title">Data Operations</h1>
      <div className="do-grid">
        <EowCard />
        <DemandImportCard />
      </div>
    </div>
  );
}
